import { RefObject, useCallback, useLayoutEffect, useRef } from 'react';

interface UseStickyBottomScrollOptions {
    keepPinnedOn?: unknown;
    forcePinOn?: unknown;
    onAwayChange?: (away: boolean) => void;
}

// Минимальное смещение пользователя от низа списка, при котором можно считать, что он сам отлистал вверх
const AWAY_THRESHOLD_PX = 10; // px

/**
 * Реализует поведение "прилипания к низу" для скролл-контейнера по ref.
 */
export const useStickyBottomScroll = (
    ref: RefObject<HTMLElement | null>,
    { keepPinnedOn, forcePinOn, onAwayChange }: UseStickyBottomScrollOptions,
) => {
    // факт "пользователь не у низа списка" — в ref, чтобы scroll-события не вызывали ре-рендер
    const isAwayRef = useRef(false);
    // latest-ref: scrollToBottom/onScroll стабильны по ссылке (deps пустые),
    // но через ref всегда вызывают свежий onAwayChange из последнего рендера
    const onAwayChangeRef = useRef(onAwayChange);
    onAwayChangeRef.current = onAwayChange;

    const scrollToBottom = useCallback(() => {
        const el = ref.current;
        if (!el) return;
        el.scrollTo({ top: el.scrollHeight });
        isAwayRef.current = false;
        onAwayChangeRef.current?.(false);
    }, [ref]);

    const onScroll = useCallback(() => {
        const el = ref.current;
        if (!el) return;
        const away = el.scrollHeight - el.scrollTop - el.clientHeight >= AWAY_THRESHOLD_PX;
        isAwayRef.current = away;
        onAwayChangeRef.current?.(away);
    }, [ref]);

    useLayoutEffect(() => {
        scrollToBottom();
    }, [scrollToBottom]);

    useLayoutEffect(() => {
        if (forcePinOn || (keepPinnedOn && !isAwayRef.current)) {
            scrollToBottom();
        }
    }, [keepPinnedOn, forcePinOn, scrollToBottom]);

    return { onScroll };
};
